import { EvidencePackButton } from "../components/EvidencePackButton";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, shortDate, EVIDENCE_TYPES } from "../lib/api";
import { PageHeader, Panel, NeonButton } from "../components/common";
import { ICONS } from "../components/EvidenceTimeline";
import { VideoEvidence } from "../components/VideoEvidence";

const sel = "bg-field border border-fieldline rounded-sm px-3 py-2 font-mono text-xs w-full focus:outline-none focus:border-brand";

const LogForm = ({ txs, onLogged }) => {
  const [form, setForm] = useState({ transaction_id: "", type: "login", description: "" });
  useEffect(() => { if (txs[0] && !form.transaction_id) setForm((f) => ({ ...f, transaction_id: txs[0].id })); }, [txs]); // eslint-disable-line
  const submit = async () => {
    if (!form.description) return toast.error("Describe the customer action");
    const { data } = await api.post("/evidence", { ...form, metadata: { source: "manual", ua: navigator.userAgent.slice(0, 60) } });
    toast.success(`Signed · ${data.hash.slice(0, 12)}`);
    setForm({ ...form, description: "" });
    onLogged();
  };
  return (
    <div className="space-y-4" data-testid="log-evidence-form">
      <div><p className="label-xs mb-1">Transaction</p>
        <select className={sel} value={form.transaction_id} onChange={(e) => setForm({ ...form, transaction_id: e.target.value })} data-testid="evidence-tx-select">
          {txs.map((t) => <option key={t.id} value={t.id}>{t.id} · {t.customer_name}</option>)}
        </select></div>
      <div><p className="label-xs mb-1">Action type</p>
        <select className={sel} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="evidence-type-select">
          {EVIDENCE_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select></div>
      <div><p className="label-xs mb-1">Description</p>
        <input className={sel} value={form.description} placeholder="e.g. Downloaded invoice PDF" onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="evidence-description-input" /></div>
      <NeonButton onClick={submit} className="w-full justify-center" data-testid="log-evidence-btn"><Plus className="w-3.5 h-3.5" /> Timestamp + sign</NeonButton>
    </div>
  );
};

const Row = ({ e }) => {
  const [v, setV] = useState(null);
  const Icon = ICONS[e.type];
  return (
    <tr className="border-b border-divider hover:bg-white/[0.02] transition-colors" data-testid={`evidence-row-${e.id}`}>
      <td className="py-2.5 pr-3 font-mono text-[11px] text-white/35 whitespace-nowrap">{shortDate(e.timestamp)}</td>
      <td className="pr-3"><span className="flex items-center gap-2 font-mono text-[11px] text-accent2">{Icon && <Icon className="w-3 h-3" />}{e.type}</span></td>
      <td className="pr-3 text-xs text-[#CCC] max-w-[420px]"><p className={e.type.startsWith("video") ? "whitespace-normal" : "truncate"}>{e.description}</p>
        {e.type === "video_verification" && e.metadata?.verification_id && <><VideoEvidence vid={e.metadata.verification_id} testid={`vault-video-${e.id}`} />
          <div className="mt-1"><EvidencePackButton txId={e.transaction_id} small label="download evidence pack" testid={`vault-pack-${e.id}`} /></div></>}</td>
      <td className="pr-3 font-mono text-[10px] text-white/30">{e.transaction_id}</td>
      <td className="pr-3 font-mono text-[10px] text-white/30">{e.hash.slice(0, 14)}…</td>
      <td><button onClick={() => api.get(`/evidence/${e.id}/verify`).then((r) => setV(r.data.valid))} data-testid={`vault-verify-${e.id}`}
        className={`font-mono text-[10px] uppercase ${v === null ? "text-white/45 hover:text-brand" : v ? "text-brand" : "text-[#F87171]"}`}>{v === null ? "verify" : v ? "valid" : "tampered"}</button></td>
    </tr>
  );
};

export default function EvidenceVault() {
  const [rows, setRows] = useState([]);
  const [txs, setTxs] = useState([]);
  const [type, setType] = useState("");
  const load = () => api.get(`/evidence?limit=80${type ? `&type=${type}` : ""}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [type]); // eslint-disable-line
  useEffect(() => { api.get("/transactions").then((r) => setTxs(r.data)); }, []);

  return (
    <div data-testid="evidence-vault">
      <PageHeader eyebrow="// cryptographic proof ledger" title="Evidence Vault">
        <select className={`${sel} w-48`} value={type} onChange={(e) => setType(e.target.value)} data-testid="evidence-filter">
          <option value="">all types</option>
          {EVIDENCE_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
      </PageHeader>
      <div className="grid grid-cols-12 gap-6">
        <Panel title="Signed event stream" className="col-span-12 xl:col-span-9">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="evidence-table">
              <thead><tr className="label-xs">{["Time", "Type", "Action", "Txn", "SHA-256", ""].map((h) => <th key={h} className="pb-3 font-normal">{h}</th>)}</tr></thead>
              <tbody>{rows.map((e) => <Row key={e.id} e={e} />)}</tbody>
            </table>
          </div>
        </Panel>
        <Panel title="Log customer action" className="col-span-12 xl:col-span-3 self-start"><LogForm txs={txs} onLogged={load} /></Panel>
      </div>
    </div>
  );
}
